import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Flex, useToast } from "@chakra-ui/react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Socket } from "socket.io-client";
import { DefaultEventsMap } from "@socket.io/component-emitter";
import { IResponse } from "../_services/interface/user.interface";
import { IClientDataResponse } from "../_services/interface/client.interface";
import { ICertificate } from "../_services/interface/certificate.interface";
import {
  getCertificates,
  getCertificatesDte,
  getCertificatesTaxCurrentAccount,
  getClientCertificates,
} from "../_services/certificate.service";
import { countUnread } from "../_services/notifications.service";
import { getResources } from "../_services/resources.service";
import { CustomAlert } from "../components/Alert/CustomAlert";
import LoadingComponent from "../components/loading";
import { ChunkErrorBoundary } from "../components/ChunkErrorBoundary";
import { escapeHtml } from "../utils/escapeHtml";
import { I18nProvider } from "../components/I18nProvider";
import { DashboardContainer } from "../components/DashboardContainer";
import Sidebar from "../components/sidebar";
import { TopNav } from "../components/TopNav/TopNav";
import { ModalContinueEditing } from "../modal/ModalContinueEditing";
import { CronometroGlobal } from "../kanban/CronometroGlobal"; // GEN-4225
import {
  ClientContext,
  NotificationContext,
  ResourcesContext,
  AppDataContext,
  ConfigGuardContext,
} from "../contexts/AppContext";
import {
  IResourcesState,
  RESOURCES_UNKNOWN,
  resourcesViewFor,
  settleResourcesFailure,
  temProdutoContaCorrente,
} from "../routes/routeAccess";
import { GovbrConnectionProvider } from "../contexts/GovbrConnectionContext";
import { PARAM_CLIENTE, comoSearch, semParams } from "../routes/deepLink";
import { persistSessionToken, readSessionFallback } from "../_services/sessionToken";

interface IAppShellProps {
  validation: IResponse;
  socket: Socket<DefaultEventsMap, DefaultEventsMap> | undefined;
  onUpdate: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Owns the authenticated app's shared state (selected client, certificates,
 * collaborator, resources, notifications) and the ConfigPanel "unsaved changes"
 * guard. Provides every context and renders the navigation-only Sidebar next to
 * the routed page (<Outlet/>).
 */
const EMPTY_NOTIFICATION_COUNTS = {
  total: 0,
  unread: 0,
  important: 0,
  folders: { INBOX: 0, ARCHIVED: 0 },
};

export function AppShell({ validation, socket, onUpdate }: IAppShellProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [currentClient, setCurrentClient] = useState<IClientDataResponse>();
  const [certificates, setCertificates] = useState<ICertificate[]>([]);
  const [certificatesTaxCurrentAccount, setCertificatesTaxCurrentAccount] = useState<ICertificate[]>([]);
  const [certificatesDte, setCertificatesDte] = useState<ICertificate[]>([]);
  const [certificatesUse, setCertificatesUse] = useState<ICertificate[]>([]);
  // GEN-4021: os resources, o status do getResources e o cliente a que os dois pertencem andam
  // juntos — ver o efeito do getResources.
  const [resourcesState, setResourcesState] = useState<IResourcesState>(RESOURCES_UNKNOWN);
  const [resourcesAttempt, setResourcesAttempt] = useState(0);
  // True once getListCertificates has finished for the current client. The route
  // guard (RequirePermission) waits for this before deciding a product gate, so an
  // empty `certificates` while still loading isn't mistaken for "no product".
  const [certificatesLoaded, setCertificatesLoaded] = useState<boolean>(false);

  // GEN-3497: o estado de recolher/expandir a sidebar subiu para cá porque agora existem
  // DOIS controles do mesmo estado (o botão da navbar e o da própria sidebar). Mantido em
  // useState local — não é preferência persistida, e sim postura da sessão.
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);

  // ConfigPanel unsaved-changes guard
  const [hasChange, setHasChange] = useState<boolean>(false);
  const [redirectToLocation, setRedirectToLocation] = useState<string>("");
  const navigationEffect = useRef<{ path: string; run?: () => void } | null>(null);
  const [openModalContinueEditing, setOpenModalContinueEditing] = useState<boolean>(false);
  const [submit, setSubmit] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<boolean>(false);
  const [configOnClose, setConfigOnClose] = useState<boolean>(false);

  // GEN-2959: cliente pedido pelo link direto (`?cliente=`) — só vale se o usuário tem acesso a ele.
  const clienteDoLink = (search: string): IClientDataResponse | undefined => {
    const guid = new URLSearchParams(search).get(PARAM_CLIENTE);
    return guid ? validation.clients.find((client) => client.guid_client === guid) : undefined;
  };

  // Emitido no tick seguinte: quando o link troca de rota (notificação in-app vindo de outra tela), o
  // efeito de `location.pathname` abaixo faz `toast.closeAll()` no mesmo commit e fecharia este aviso
  // na hora — a pessoa nem saberia que mudou de cliente (GEN-2959).
  const avisarClienteDoLink = (client: IClientDataResponse) => setTimeout(() => avisarAgora(client), 0);

  const avisarAgora = (client: IClientDataResponse) => {
    toast({
      status: "info",
      duration: 6000,
      isClosable: true,
      containerStyle: { maxW: "800px" },
      render: ({ onClose }) => (
        <CustomAlert
          closableFunc={onClose}
          descriptionHtml={`<div><b>Você está agora no cliente ${escapeHtml(
            client.name
          )}</b></div>O link aberto pertence a este cliente.`}
          icon={true}
          status={"info"}
          variant={"alert"}
          type={"three"}
          className={"alert-instance"}
        />
      ),
    });
  };

  // As default a company need to be set, and need to be the company that the user is owner
  useEffect(() => {
    // GEN-2959: link direto de um registro de outro cliente — abre já nele, sem passar pelo padrão.
    // O aviso só aparece quando o link mudou o cliente que o usuário veria normalmente.
    const doLink = clienteDoLink(location.search);
    const isOwner = validation.clients.find((_x) => _x.permissions.includes("org.owner"));
    const padrao =
      isOwner ?? validation.clients.find((_x) => _x.permissions.includes("org.admin")) ?? validation.clients[0];
    if (doLink) {
      setCurrentClient(doLink);
      if (doLink.guid_client !== padrao?.guid_client) avisarClienteDoLink(doLink);
    } else if (isOwner) {
      setCurrentClient(isOwner);
    } else {
      //If not the company that this user is administrator
      const isAdmin = validation.clients.find((_x) => _x.permissions.includes("org.admin"));
      if (isAdmin) {
        setCurrentClient(isAdmin);
      } else {
        //If nothing works, the first company of the list
        setCurrentClient(validation.clients[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const centralDeNotificacoes = /^\/notificacoes(?:\/|$)/.test(location.pathname);

  const selectClient = (guidClient: string, preservarRegistro = false) => {
    // Navegador com storage bloqueado lança aqui: a troca de cliente segue mesmo assim (senão a troca
    // automática pelo link travava com `cliente=` na URL e a tela esperando) — mesmo trato do sessionToken.
    try {
      localStorage.clear();
    } catch {
      // sem storage, não há o que limpar
    }
    if (centralDeNotificacoes) {
      const params = new URLSearchParams(location.search);
      params.set(PARAM_CLIENTE, guidClient);
      navigate(
        { pathname: preservarRegistro ? location.pathname : "/notificacoes", search: comoSearch(params) },
        { replace: true }
      );
    }
    setCurrentClient(validation.clients.find((client) => client.guid_client === guidClient));
  };

  // GEN-2959: `cliente=` na URL — no primeiro carregamento (já aplicado acima) ou com o app aberto
  // (notificação in-app, link colado na barra). Com o app aberto, é a mesma troca do seletor de
  // clientes. Sem acesso ao cliente pedido, só limpa: a tela não acha o registro no cliente ativo e
  // avisa "não encontrado", sem expor nada de outro cliente. As telas esperam o param sumir para
  // resolver o registro (`useRecordDeepLink`), então a busca já sai no cliente certo.
  useEffect(() => {
    if (!currentClient) return;
    const temClienteNoLink = new URLSearchParams(location.search).has(PARAM_CLIENTE);
    if (!temClienteNoLink && !centralDeNotificacoes) return;
    const doLink = clienteDoLink(location.search);
    if (doLink && doLink.guid_client !== currentClient.guid_client) {
      // `selectClient` limpa o localStorage — e é nele que mora a cópia da sessão para quem tem cookie
      // bloqueado por política do navegador (GEN-3111, caso Bradesco). Pelo seletor a pessoa escolhe trocar;
      // pelo link a troca é automática, então a sessão não pode ir junto: o próximo F5 cairia no login.
      const sessao = readSessionFallback();
      selectClient(doLink.guid_client, true);
      if (sessao) persistSessionToken(sessao);
      avisarClienteDoLink(doLink);
    }
    // O aviso tem endereço permanente. Manter o cliente na Central faz F5/compartilhar
    // reabrirem a mesma caixa, mesmo quando este não é o cliente padrão do usuário.
    if (centralDeNotificacoes) {
      if (doLink) return;
      const params = new URLSearchParams(location.search);
      params.set(PARAM_CLIENTE, currentClient.guid_client);
      navigate({ pathname: location.pathname, search: comoSearch(params), hash: location.hash }, { replace: true });
      return;
    }
    navigate(
      {
        pathname: location.pathname,
        search: comoSearch(semParams(location.search, [PARAM_CLIENTE])),
        hash: location.hash,
      },
      { replace: true, state: location.state }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, currentClient]);

  // GEN-4021: derivados NO RENDER (não em efeito), como `hasCertificate`/`hasCpfProduct` e como os
  // resources: num efeito, o render da troca de cliente casava o colaborador do cliente anterior com
  // os dados do novo — um frame com a régua de jurisdição do cliente errado.
  const collaborator: IClientDataResponse | undefined = useMemo(
    () =>
      validation.companies.length > 0
        ? validation.clients.find((_x) => _x.guid_client === currentClient?.guid_client)
        : undefined,
    [currentClient, validation]
  );

  // GEN-3984: o módulo Conta Corrente existe para quem contratou QUALQUER consulta dele (Situação
  // Fiscal ou CADIN Federal); cada sub-página confere a sua (`subpaginasDoCliente`).
  const hasTaxCurrentAccountProduct = useMemo(
    () => validation.companies.length > 0 && temProdutoContaCorrente(currentClient?.products),
    [currentClient, validation]
  );

  // O token muda no render da troca, inclusive A→B→A; nenhum frame usa o contador anterior.
  const colaboradorAtual = useRef({ guid: collaborator?.guid_collaborator });
  if (colaboradorAtual.current.guid !== collaborator?.guid_collaborator) {
    colaboradorAtual.current = { guid: collaborator?.guid_collaborator };
  }
  const revisaoDoContador = useRef(0);
  const updateNotificationContext = (obj: any) => {
    revisaoDoContador.current += 1;
    setNotificationValues((previousState: any) => ({
      ...(previousState.contexto === colaboradorAtual.current ? previousState : EMPTY_NOTIFICATION_COUNTS),
      ...obj,
      contexto: colaboradorAtual.current,
      updateNotificationContext,
    }));
  };

  const [notificationValues, setNotificationValues] = useState({
    ...EMPTY_NOTIFICATION_COUNTS,
    contexto: colaboradorAtual.current,
    updateNotificationContext,
  });
  const notificationContextValues =
    notificationValues.contexto === colaboradorAtual.current
      ? notificationValues
      : { ...EMPTY_NOTIFICATION_COUNTS, updateNotificationContext };

  const getNotifications = async () => {
    if (!collaborator) return;

    const alvo = collaborator.guid_collaborator;
    const contexto = colaboradorAtual.current;
    const revisao = revisaoDoContador.current;
    const result = await countUnread(alvo);
    // A consulta inicial não pode desfazer uma ação nem aplicar o contador de outro cliente.
    if (colaboradorAtual.current !== contexto || revisaoDoContador.current !== revisao) return;

    if (result.status === 200 && typeof result.response === "number") {
      updateNotificationContext({ unread: result.response });
    } else {
      toast({
        status: "error",
        duration: 5000,
        isClosable: true,
        containerStyle: {
          maxW: "800px",
        },
        render: ({ onClose }) => (
          <CustomAlert
            closableFunc={onClose}
            descriptionHtml={`<div><b>Ocorreu um erro</b></div>Ocorreu um erro ao buscar as notificações.`}
            icon={true}
            status={"error"}
            variant={"alert"}
            type={"three"}
            className={"alert-instance"}
          />
        ),
      });
    }
  };

  useEffect(() => {
    if (collaborator) {
      getNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collaborator]);

  const getListCertificates = async (guid_client: string) => {
    try {
      const { status: status1, response: response1 } = await getCertificates();
      if (status1 === 200) {
        setCertificates(response1 as ICertificate[]);
      }

      const { status: statusCertificateInUse, response: responseCertificateInUse } = await getClientCertificates(
        guid_client
      );
      if (statusCertificateInUse === 200) {
        setCertificatesUse(responseCertificateInUse as ICertificate[]);
      }

      const { status: statusCertificateTaxCurrentAccount, response: responseCertificateTaxCurrentAccount } =
        await getCertificatesTaxCurrentAccount();
      if (statusCertificateTaxCurrentAccount === 200) {
        setCertificatesTaxCurrentAccount(responseCertificateTaxCurrentAccount as ICertificate[]);
      }

      const { status: statusDte, response: responseDte } = await getCertificatesDte();
      if (statusDte === 200) {
        setCertificatesDte(responseDte as ICertificate[]);
      }
    } finally {
      // Settle the gate even on error so the guard never hangs on null.
      setCertificatesLoaded(true);
    }
  };

  useEffect(() => {
    if (currentClient) {
      setCertificatesLoaded(false);
      getListCertificates(currentClient.guid_client);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClient]);

  // Derived synchronously (not in an effect) so the values are always consistent
  // with `certificates`/`currentClient` in the same render — the route guard can
  // trust them as soon as `certificatesLoaded` is true, with no extra render lag.
  const hasCpfProduct = useMemo(() => {
    if (!currentClient) return false;
    const productsCpf = currentClient.products_cpf ? currentClient.products_cpf : {};
    return certificates.some((certificate) => {
      let hasProduct =
        productsCpf?.hasOwnProperty(certificate.slug as string) &&
        currentClient?.products_cpf[certificate.slug as keyof typeof currentClient.products_cpf];
      if (!hasProduct && certificate.children) {
        hasProduct = certificate.children.some(
          (child) =>
            productsCpf?.hasOwnProperty(child.slug as string) &&
            currentClient?.products_cpf[child.slug as keyof typeof currentClient.products_cpf]
        );
      }
      return !!hasProduct;
    });
  }, [currentClient, certificates]);

  const hasCertificate = useMemo(() => {
    return certificates.some((certificate) => {
      let hasProduct = currentClient?.products[certificate.slug as keyof typeof currentClient.products];
      if (!hasProduct && certificate.children) {
        hasProduct = certificate.children.some((child) => currentClient?.products[child.slug as string]);
      }
      return !!hasProduct;
    });
  }, [currentClient, certificates]);

  // GEN-4021: a regra de jurisdição só pode confiar nos resources depois que o getResources
  // respondeu PARA ESTE cliente. Status e lista são derivados NO RENDER do estado do cliente a que
  // pertencem: na troca de cliente — já no render da troca, antes de este efeito rodar — o novo é
  // "carregando" com lista vazia, nunca o "carregado" do anterior. Ao voltar a um cliente cujo
  // resultado ainda é o guardado, ele vale (é DESTE cliente) enquanto a busca nova não responde.
  // Uma resposta atrasada do cliente anterior é descartada. Um refresh do MESMO cliente (o objeto muda, o guid
  // não) rebusca sem esconder o menu no meio. `resourcesAttempt` é o "Tentar novamente" do aviso.
  const { status: resourcesStatus, resources } = resourcesViewFor(resourcesState, currentClient?.guid_client);
  useEffect(() => {
    const guid = currentClient?.guid_client;
    if (!guid) return;
    let stale = false;
    (async () => {
      try {
        const result = await getResources(guid);
        if (stale) return;
        if (result.status === 200 && Array.isArray(result.response)) {
          setResourcesState({ guid, status: "loaded", resources: result.response });
        } else {
          setResourcesState((s) => settleResourcesFailure(s, guid));
        }
      } catch {
        if (!stale) setResourcesState((s) => settleResourcesFailure(s, guid));
      }
    })();
    return () => {
      stale = true;
    };
  }, [currentClient, resourcesAttempt]);

  // O Desfazer pertence à Central, inclusive após abrir ou fechar seu leitor.
  // Sair do módulo ou trocar de cliente encerra os avisos daquele contexto.
  const contextoDosToasts = centralDeNotificacoes
    ? `/notificacoes:${currentClient?.guid_client ?? ""}`
    : location.pathname;
  useEffect(() => {
    toast.closeAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextoDosToasts]);

  // Browser-level guard for leaving the document with unsaved ConfigPanel changes
  // (F5/reload, tab close, typing another URL). In-app navigation funnels through
  // requestNavigation; this is the only hook left for full-page unloads. NOTE: it
  // does NOT catch in-app Back/Forward — react-router's useBlocker needs a data
  // router (createBrowserRouter), and the app still uses BrowserRouter.
  useEffect(() => {
    if (!hasChange) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasChange]);

  // ConfigPanel guard: navigation funnels through requestNavigation so we can
  // intercept while there are unsaved changes (replaces the old handlePages).
  const navigateTo = (path: string) => {
    const confirmed = navigationEffect.current;
    navigationEffect.current = null;
    navigate(path);
    setRedirectToLocation("");
    setHasChange(false);
    if (confirmed?.path === path) confirmed.run?.();
  };

  const requestNavigation = (path: string, onNavigate?: () => void) => {
    // The effect belongs to this attempt: cancel/error/replacement must never consume it.
    navigationEffect.current = { path, run: onNavigate };
    if (hasChange) {
      setRedirectToLocation(path);
      setOpenModalContinueEditing(true);
      return;
    }
    navigateTo(path);
  };

  useEffect(() => {
    // Navigate only after the save actually cleared the pending changes
    // (hasChange === false). On "Salvar e sair" the section flips hasChange off on
    // success and sets submitError on failure; gating on !hasChange keeps us on the
    // page when the save fails, instead of leaving (and silently dropping changes).
    if (!submit && !hasChange && !!redirectToLocation && redirectToLocation !== location.pathname) {
      navigateTo(redirectToLocation);
      setConfigOnClose(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submit, hasChange]);

  useEffect(() => {
    if (submitError) {
      setSubmit(false);
      setSubmitError(false);
      navigationEffect.current = null;
      setRedirectToLocation(location.pathname);
      setConfigOnClose(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitError]);

  const appData = {
    validation,
    socket,
    onUpdate,
    currentClient,
    guid_client: currentClient?.guid_client as string,
    collaborator,
    selectClient,
    resources,
    resourcesLoaded: resourcesStatus === "loaded",
    resourcesError: resourcesStatus === "error",
    certificates,
    certificatesUse,
    certificatesTaxCurrentAccount,
    certificatesDte,
    certificatesLoaded,
    hasCpfProduct,
    hasCertificate,
    hasTaxCurrentAccountProduct,
  };

  const configGuard = {
    hasChange,
    setHasChange,
    submit,
    setSubmit,
    submitError,
    setSubmitError,
    onClose: configOnClose,
    setOnClose: setConfigOnClose,
    requestNavigation,
  };

  return (
    <DashboardContainer translate="no">
      <I18nProvider currentClient={currentClient}>
        <ClientContext.Provider
          value={{ guid_client: currentClient?.guid_client ?? null, client_name: currentClient?.name ?? null }}
        >
          <NotificationContext.Provider value={notificationContextValues}>
            <ResourcesContext.Provider
              value={{
                resources,
                resourcesLoaded: resourcesStatus === "loaded",
                resourcesError: resourcesStatus === "error",
              }}
            >
              <AppDataContext.Provider value={appData}>
                <ConfigGuardContext.Provider value={configGuard}>
                  {/* GEN-3754 — estado das conexões gov.br em segundo plano: global, porque o
                      ícone da barra acompanha de qualquer tela. */}
                  <GovbrConnectionProvider guidClient={currentClient?.guid_client} socket={socket}>
                    <Flex w="100%" h="100vh" overflow="hidden">
                      {/* A sidebar volta a ocupar a altura inteira, com o logo e o botão de
                        recolher dela — a barra global NÃO passa por cima dela. */}
                      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

                      {/* Coluna de conteúdo: a faixa global em cima, a página rolando embaixo.
                        A faixa fica FORA do `.mainPage` de propósito — `.mainPage` é o
                        scrollParent de que o Datatables e o Dashboard dependem, e a barra
                        não pode rolar junto com a tabela. */}
                      <Flex
                        direction="column"
                        flexGrow="1"
                        flexShrink="1"
                        minW="0"
                        h="100%"
                        overflow="hidden"
                        transition="300ms"
                        // Sem paddingLeft aqui: a sidebar secundária de Configurações usa
                        // `marginLeft: -23px` para colar na borda, contando com o padding do
                        // `.mainPage`. Com o padding no wrapper (que tem overflow hidden), a
                        // margem negativa saía da caixa e o menu era CORTADO.
                        style={{ backgroundColor: "#f8fafc", borderTopLeftRadius: "20px" }}
                      >
                        <TopNav />

                        {/* GEN-4021: sem os resources, Certidões, Caixa Postal e Conta Corrente ficam
                            ocultos (fail-closed) — o aviso diz por quê e oferece a nova tentativa, em
                            vez de o menu simplesmente sumir. Fica fora do `.mainPage` para não mexer
                            no layout das páginas. */}
                        {resourcesStatus === "error" ? (
                          <CustomAlert
                            className={"alert-instance"}
                            status={"warning"}
                            descriptionHtml={
                              "Não foi possível carregar as permissões por jurisdição. Certidões, Caixa Postal e Conta Corrente ficam ocultos até o carregamento."
                            }
                            clicable={{ action: () => setResourcesAttempt((n) => n + 1), text: "Tentar novamente" }}
                            type={"banner"}
                            variant={"alert"}
                            isClosable={false}
                            style={{ margin: "8px 23px 0", width: "auto" }}
                          />
                        ) : null}

                        {/* --- MAIN PAGE --- */}
                        {/* Hold the routed page until a client is selected: pages (and
                          useClientContext) assume a non-null client, so mounting before
                          currentClient resolves would crash on a deep-link/F5. */}
                        <Flex
                          style={{ backgroundColor: "#f8fafc", paddingLeft: "23px", overflowY: "auto" }}
                          className="mainPage"
                          flexGrow="1"
                          flexShrink="1"
                          minH="0"
                        >
                          {/* GEN-2959 (PERF-05): as páginas são chunks lazy — o loading padrão
                              aparece só enquanto o chunk da rota baixa. */}
                          {currentClient ? (
                            // Chunk que não carrega (deploy no meio da sessão, rede) vira aviso com
                            // saída, não tela branca. `key` pelo 1º segmento da rota: trocar de TELA tenta de
                            // novo, sem remontar a navegação interna (cards do Kanban 2.0, seções de Config).
                            <ChunkErrorBoundary key={location.pathname.split("/")[1]} oQue="esta tela">
                              <Suspense fallback={<LoadingComponent style={{ width: "100%" }} />}>
                                <Outlet />
                              </Suspense>
                            </ChunkErrorBoundary>
                          ) : null}
                        </Flex>
                      </Flex>
                    </Flex>

                    {/* GEN-4225: cronômetro do Kanban 2.0 visível em todas as telas (flutua no canto). */}
                    {currentClient ? <CronometroGlobal /> : null}

                    <ModalContinueEditing
                      callback={navigateTo}
                      open={openModalContinueEditing}
                      openHook={setOpenModalContinueEditing}
                      location={redirectToLocation}
                      callbackCancel={() => {
                        navigationEffect.current = null;
                        setRedirectToLocation("");
                      }}
                      callbackSubmit={(value) => {
                        setRedirectToLocation(value);
                        setSubmit(true);
                      }}
                      onClose={configOnClose}
                      setOnClose={setConfigOnClose}
                      submitError={submitError}
                    />
                  </GovbrConnectionProvider>
                </ConfigGuardContext.Provider>
              </AppDataContext.Provider>
            </ResourcesContext.Provider>
          </NotificationContext.Provider>
        </ClientContext.Provider>
      </I18nProvider>
    </DashboardContainer>
  );
}
